import React from 'react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import {AppTitle, AppText} from '../ui/Typography';
import {X, CheckCircle, XCircle} from 'lucide-react';

interface ActionButton {
  label: string;
  onClick: () => void;
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost';
  color?: 'blue' | 'green' | 'red' | 'gray';
  icon?: React.ReactNode;
  disabled?: boolean;
}

interface DetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  width?: string;
  showCloseButton?: boolean;
  actionButtons?: ActionButton[];
  loading?: boolean;
  className?: string;
}

const DetailsModal: React.FC<DetailsModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  width = 'max-w-4xl',
  showCloseButton = true,
  actionButtons = [],
  loading = false,
  className = ''
}) => {
  const getButtonStyles = (button: ActionButton) => {
    const baseStyles = 'flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors';

    if (button.variant === 'ghost') {
      return `${baseStyles} text-gray-600 hover:text-gray-800 hover:bg-gray-100`;
    }

    if (button.variant === 'outline') {
      const colorMap = {
        blue: 'border-brand-300 text-brand-600 hover:bg-brand-50',
        green: 'border-green-300 text-green-600 hover:bg-green-50',
        red: 'border-red-300 text-red-600 hover:bg-red-50',
        gray: 'border-gray-300 text-gray-600 hover:bg-gray-50'
      };
      return `${baseStyles} border ${colorMap[button.color || 'gray']}`;
    }

    if (button.variant === 'secondary') {
      const colorMap = {
        blue: 'bg-brand-100 text-brand-800 hover:bg-brand-200',
        green: 'bg-green-100 text-green-800 hover:bg-green-200',
        red: 'bg-red-100 text-red-800 hover:bg-red-200',
        gray: 'bg-gray-100 text-gray-800 hover:bg-gray-200'
      };
      return `${baseStyles} ${colorMap[button.color || 'gray']}`;
    }

    // Primary variant
    const colorMap = {
      blue: 'bg-brand-600 text-white hover:bg-brand-700',
      green: 'bg-green-600 text-white hover:bg-green-700',
      red: 'bg-red-600 text-white hover:bg-red-700',
      gray: 'bg-gray-600 text-white hover:bg-gray-700'
    };
    return `${baseStyles} ${colorMap[button.color || 'blue']}`;
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title={title}
      width={width}
    >
      <div className={`space-y-6 ${className}`}>
        {/* Content */}
        <div className="max-h-[70vh] overflow-y-auto">
          {children}
        </div>

        {/* Action Buttons */}
        {(actionButtons.length > 0 || showCloseButton) && (
          <div className="flex justify-between items-center pt-4 border-t border-gray-200">
            <div className="flex gap-3">
              {actionButtons.map((button, index) => (
                <Button
                  key={index}
                  onClick={button.onClick}
                  disabled={button.disabled || loading}
                  isLoading={loading}
                  className={getButtonStyles(button)}
                >
                  {button.icon}
                  {button.label}
                </Button>
              ))}
            </div>

            {showCloseButton && (
              <Button
                variant="ghost"
                onClick={onClose}
                disabled={loading}
              >
                إغلاق
              </Button>
            )}
          </div>
        )}

        {/* Loading Overlay */}
        {loading && (
          <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 border-2 border-brand-600 border-t-transparent rounded-full animate-spin"></div>
              <AppText className="text-sm text-gray-600">جاري التحميل...</AppText>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default DetailsModal;
